import React, { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { BankIdApiService } from "@api-services/integrations/BankIdApiService";
import { BankIdAuthType } from "@data-types/user/BankIdAuthType";
import { useLoading, useRootAppStore } from "@hooks";
import { MobilePlatform } from "@state/stores/types";
import {
	getRedirectOriginUrl,
	isCapacitorPlatform,
} from "@utils/specific/capacitor/helpers";
import classnames from "classnames";
import { observer } from "mobx-react";

export default observer(function LogonBankIdButton(props: {
	btnSize?: string;
}) {
	const { setIsLoading } = useLoading();
	const { t } = useTranslation("login");
	const [isButtonDisabled, setIsButtonDisabled] = useState(false);

	const rootStore = useRootAppStore<MobilePlatform>();
	const buttonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (!isCapacitorPlatform()) {
			return;
		}

		if (
			!rootStore.bankIdCapacitorStore.isBankIdFlowPopupActive &&
			!rootStore.bankIdCapacitorStore.isBankIdQrCodePopupActive
		) {
			buttonRef.current?.focus();
		}
	}, [
		rootStore?.bankIdCapacitorStore?.isBankIdFlowPopupActive,
		rootStore?.bankIdCapacitorStore?.isBankIdQrCodePopupActive,
	]);

	async function redirectToBankId() {
		if (isCapacitorPlatform()) {
			rootStore.bankIdCapacitorStore.activateFlowPopup(
				BankIdAuthType.Login
			);
			return;
		}

		setIsLoading(true);
		setIsButtonDisabled(true);

		const successRedirectUrl = getRedirectOriginUrl(
			window.location.pathname,
			true
		);

		const failRedirectUrl = getRedirectOriginUrl(
			window.location.pathname + "?failedAuth=true",
			true
		);

		try {
			const bankIdAuthResponse =
				await BankIdApiService.getBankIdWidgetInfo(
					"same-device",
					successRedirectUrl,
					failRedirectUrl
				);

			if (bankIdAuthResponse.redirectUrl) {
				window.location.href = bankIdAuthResponse.redirectUrl;
			}
		} catch (e) {
			console.error(e);
			setIsButtonDisabled(false);
			setIsLoading(false);
		}
	}

	if (!WEBPACK_IS_BANKID_ENABLED) {
		return null;
	}

	return (
		<div>
			<button
				ref={buttonRef}
				disabled={isButtonDisabled || rootStore.isIpRestricted}
				className={classnames(
					"at-logInWithBankID btn",
					props.btnSize ? `btn--${props.btnSize}` : "btn--base",
					"btn--block btn--primary--inverted"
				)}
				onClick={redirectToBankId}
			>
				{t<string>("LOGON_WITH")} BankID
				<i className="u-icon u-icon--bankid u-mar--left--tny" />
			</button>
			<div className="u-mar--top--tny u-type--sml u-type--center u-color--text--secondary">
				{t<string>("FOR_MORE_INFORMATION_VISIT")}{" "}
				<a
					className="u-anchor u-anchor--primary"
					href="https://www.bankid.com"
				>
					BankID
				</a>{" "}
				{t<string>("WEBSITE")}.
			</div>
		</div>
	);
});
